import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { RCText, RCTextWithIcon, RCBox } from '@reverbdotcom/cadence/components';
import { ReverbLogoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { PriceDisplay } from '@reverbdotcom/commons';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import {
  core_apimessages_Money as Money,
  core_apimessages_MyShopOnboardingResponse_PaymentMethod,
} from '@reverbdotcom/commons/src/gql/graphql';

import { Paths } from '../../../components/shared/url_helpers';

export const WALLET_HC_ID = '43454547005075';

interface Props {
  walletBalance: Money,
  reverbCreditsBalances: Money[],
  payoutBalances: Money[],
  paymentMethod: string;
}

function WalletBalance({ balance }) {
  return (
    <div className="d-flex fx-align-center fx-justify-between mb-2">
      <div className="d-flex fx-align-center text-color-vu-orange-600">
        <RCTextWithIcon svgComponent={ReverbLogoCircleIcon} placement="left">
          <RCText.Utility
            size="350"
            color="rc-color-text-secondary"
            weight="semibold"
            htmlTag="span"
          >
            {I18n.t('discovery.reverbHeader.userMenu.wallet')}
          </RCText.Utility>
        </RCTextWithIcon>
      </div>
      <div className={classNames(
        'reverb-header__menu__combined_credits__balance',
        'reverb-header__menu__combined_credits__wallet-balance',
        { 'reverb-header__menu__combined_credits__balance-zero': balance.amountCents == 0 },
      )}>
        <PriceDisplay display={balance.display} />
      </div>
    </div>
  );
}

export default function ReverbHeaderCreditCallouts({ walletBalance, reverbCreditsBalances, payoutBalances, paymentMethod }: Props) {
  const walletHcArticleLink = useHelpCenterArticleUrl(WALLET_HC_ID);

  if (!walletBalance && !reverbCreditsBalances?.length && !payoutBalances?.length) { return null; }

  const myEarningsPayoutsPath = Paths.myEarningsPayouts.expand({});
  const creditsOtherThanWallet = reverbCreditsBalances
    .filter((balance) => balance.currency != walletBalance?.currency);

  return (
    <div className="bdb-1 bd-primary pt-2 plr-2">
      {walletBalance && (
        <>
          <div className="mtb-2">
            <RCBox
              colorFamily="vu-orange"
              variant="subtle-outlined"
              padding="rc-space-2"
              borderRadius="rc-border-radius-md"
              display="block"
            >
              <div className="ws-normal text-fine-300" style={{ minWidth: '25rem' }}>
                <Translate
                  tag="div"
                  text="discovery.reverbHeader.userMenu.walletCalloutHtml"
                  args={{ linkOpen: `<a href="${walletHcArticleLink}" class="td-underline" style="display: inline">`, linkClose: '</a>' }}
                  html
                />
              </div>
            </RCBox>
          </div>

          {paymentMethod == core_apimessages_MyShopOnboardingResponse_PaymentMethod.REVERB_PAYMENTS && (
            <CoreLink
              to={myEarningsPayoutsPath}
            >
              <WalletBalance
                balance={walletBalance}
              />
            </CoreLink>
          )}

          {paymentMethod != core_apimessages_MyShopOnboardingResponse_PaymentMethod.REVERB_PAYMENTS && (
            <WalletBalance
              balance={walletBalance}
            />
          )}
        </>
      )}

      {!!creditsOtherThanWallet && (
        creditsOtherThanWallet.map(({ display, currency }, index) => (
          <div className="d-flex fx-align-center fx-justify-between mb-2" key={`reverb_credits_${currency}`}>
            {index == 0 ? (
              <RCText.Utility size="350" color="rc-color-text-secondary" weight="semibold">
                {I18n.t('discovery.reverbHeader.userMenu.credits')}
              </RCText.Utility>
            ) : <div/>}
            <div className="reverb-header__menu__combined_credits__balance reverb-header__menu__combined_credits__credit-balance">
              <PriceDisplay display={display}/>
            </div>
          </div>
        ))
      )}

      {!!payoutBalances && (
        payoutBalances.map(({ display, currency }, index) => (
          <div className="d-flex fx-align-center fx-justify-between mb-2" key={`payout_balances_${currency}`}>
            {index == 0 ? (
              <RCText.Utility size="350" color="rc-color-text-secondary" weight="semibold">
                {I18n.t('discovery.reverbHeader.userMenu.payout')}
              </RCText.Utility>
            ) : <div/>}
            <div className="reverb-header__menu__combined_credits__balance reverb-header__menu__combined_credits__payout-balance">
              <PriceDisplay display={display} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
